// UltraFlex Admin Portal - Billing & Collections
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CreditCard, AlertTriangle, CheckCircle, XCircle, Clock,
  RefreshCw, ChevronLeft, ChevronRight, Download, Filter,
  DollarSign, TrendingUp, TrendingDown, Calendar, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBilling = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('collections');
  const [collections, setCollections] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab, page, statusFilter, siteFilter]);

  useEffect(() => {
    fetch(`${API_URL}/api/sites`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setSites(d.items || [])).catch(() => {});
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'collections') {
        const params = new URLSearchParams({ page: page.toString(), page_size: '20' });
        if (statusFilter) params.append('status', statusFilter);
        
        const res = await fetch(`${API_URL}/api/collections?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setCollections(data.items || []);
        setTotalPages(data.total_pages || 1);
      } else if (activeTab === 'mandates') {
        const params = new URLSearchParams({ page: page.toString(), page_size: '20' });
        if (statusFilter) params.append('status', statusFilter);
        
        const res = await fetch(`${API_URL}/api/mandates?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setMandates(data.items || []);
        setTotalPages(data.total_pages || 1);
      } else if (activeTab === 'exceptions') {
        const res = await fetch(`${API_URL}/api/collections/exceptions?page=${page}&page_size=20`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setExceptions(data.items || []);
        setTotalPages(data.total_pages || 1);
      }

      // Fetch revenue stats
      const statsRes = await fetch(`${API_URL}/api/reports/revenue`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (collectionId) => {
    const newDate = prompt('Enter new collection date (YYYY-MM-DD):');
    if (!newDate) return;

    try {
      await fetch(`${API_URL}/api/collections/${collectionId}/retry?new_date=${newDate}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Collection retry scheduled');
      fetchData();
    } catch (error) {
      alert('Failed to retry collection');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      successful: { color: 'green', icon: CheckCircle },
      scheduled: { color: 'blue', icon: Clock },
      submitted: { color: 'yellow', icon: Clock },
      failed: { color: 'red', icon: XCircle },
      refunded: { color: 'purple', icon: RefreshCw },
      active: { color: 'green', icon: CheckCircle },
      pending: { color: 'yellow', icon: Clock },
      cancelled: { color: 'gray', icon: XCircle }
    };
    const c = config[status] || { color: 'gray', icon: Clock };
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${c.color}-500/20 text-${c.color}-400`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-uf-black">
      {/* Header */}
      <div className="bg-uf-surface border-b border-uf-border px-4 md:px-8 py-4 md:py-6">
        <h1 className="text-2xl font-bold text-uf-text">Billing & Collections</h1>
        <p className="text-uf-text-muted mt-1">Manage Direct Debit collections and mandates</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="px-4 md:px-8 py-4 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            title="Total Collected" 
            value={`£${stats.collections?.successful?.total?.toLocaleString() || 0}`}
            icon={TrendingUp}
            color="green"
          />
          <StatCard 
            title="Failed" 
            value={`£${stats.collections?.failed?.total?.toLocaleString() || 0}`}
            icon={TrendingDown}
            color="red"
          />
          <StatCard 
            title="Refunded" 
            value={`£${stats.collections?.refunded?.total?.toLocaleString() || 0}`}
            icon={RefreshCw}
            color="purple"
          />
          <StatCard 
            title="Net Revenue" 
            value={`£${stats.net_revenue?.toLocaleString() || 0}`}
            icon={DollarSign}
            color="blue"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="px-4 md:px-8 border-b border-uf-border overflow-x-auto">
        <div className="flex space-x-4 md:space-x-8 min-w-0">
          {[
            { id: 'collections', label: 'Collections' },
            { id: 'mandates', label: 'Mandates' },
            { id: 'exceptions', label: 'Exceptions Queue', badge: exceptions.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setPage(1); setStatusFilter(''); }}
              data-testid={`billing-tab-${tab.id}`}
              className={`py-4 px-2 border-b-2 transition-colors flex items-center whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'border-uf-red text-uf-red' 
                  : 'border-transparent text-uf-text-muted hover:text-uf-text'
              }`}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 md:px-8 py-4 bg-uf-surface-hi border-b border-uf-border flex flex-wrap items-center gap-4">
        <Filter className="w-5 h-5 text-uf-text-muted" />
        {activeTab === 'collections' && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text"
          >
            <option value="">All Status</option>
            <option value="scheduled">Scheduled</option>
            <option value="submitted">Submitted</option>
            <option value="successful">Successful</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        )}
        {activeTab === 'mandates' && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="cancelled">Cancelled</option>
          </select>
        )}
        <button 
          onClick={async () => {
            try {
              const endpoint = activeTab === 'collections' ? 'collections' : activeTab === 'mandates' ? 'billing' : 'collections';
              const res = await fetch(`${API_URL}/api/finance/export/${endpoint}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${activeTab}_export.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
              } else {
                alert('Export failed');
              }
            } catch { alert('Export failed'); }
          }}
          data-testid="billing-export-csv"
          className="ml-auto flex items-center px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text hover:bg-uf-surface-hi"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </button>
        <select data-testid="billing-site-filter" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}
          className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text">
          <option value="">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* Content */}
      <div className="p-4 md:p-8">
        <div className="bg-uf-surface rounded-lg border border-uf-border overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto"></div>
            </div>
          ) : (
            <>
              {activeTab === 'collections' && (
                <CollectionsTable 
                  collections={collections} 
                  getStatusBadge={getStatusBadge}
                  onRetry={handleRetry}
                />
              )}
              {activeTab === 'mandates' && (
                <MandatesTable mandates={mandates} getStatusBadge={getStatusBadge} token={token} onRefresh={fetchData} />
              )}
              {activeTab === 'exceptions' && (
                <ExceptionsTable 
                  exceptions={exceptions} 
                  onRetry={handleRetry}
                />
              )}
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-uf-surface-hi border-t border-uf-border flex items-center justify-between">
              <span className="text-uf-text-muted text-sm">Page {page} of {totalPages}</span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded bg-uf-surface border border-uf-border text-uf-text disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded bg-uf-surface border border-uf-border text-uf-text disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-uf-surface rounded-lg p-5 border border-uf-border">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-uf-text-muted text-sm">{title}</p>
        <p className="text-2xl font-bold text-uf-text mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-lg bg-${color}-500/10`}>
        <Icon className={`w-6 h-6 text-${color}-500`} />
      </div>
    </div>
  </div>
);

const CollectionsTable = ({ collections, getStatusBadge, onRetry }) => (
  <div className="admin-table-wrap">
  <table className="w-full">
    <thead>
      <tr className="bg-uf-surface-hi">
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Reference</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Member</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Amount</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Date</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Status</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Actions</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-uf-border">
      {collections.length === 0 ? (
        <tr>
          <td colSpan="6" className="px-6 py-12 text-center text-uf-text-muted">
            No collections found
          </td>
        </tr>
      ) : (
        collections.map((col) => (
          <tr key={col.id} className="hover:bg-uf-surface-hi">
            <td className="px-6 py-4 font-mono text-sm text-uf-text">{col.reference}</td>
            <td className="px-6 py-4 text-uf-text">{col.member_id?.slice(0, 8)}...</td>
            <td className="px-6 py-4 text-right text-uf-text font-medium">£{col.amount?.toFixed(2)}</td>
            <td className="px-6 py-4 text-uf-text-muted">{col.collection_date}</td>
            <td className="px-6 py-4">{getStatusBadge(col.status)}</td>
            <td className="px-6 py-4 text-right">
              {col.status === 'failed' && (
                <button 
                  onClick={() => onRetry(col.id)}
                  className="text-uf-red hover:text-uf-red-hover text-sm"
                >
                  Retry
                </button>
              )}
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
  </div>
);

const MandatesTable = ({ mandates, getStatusBadge, token, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const filtered = mandates.filter(m => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (m.account_holder_name || '').toLowerCase().includes(term) ||
      (m.member_name || '').toLowerCase().includes(term) ||
      (m.mandate_reference || '').toLowerCase().includes(term) ||
      (m.bureau_mandate_id || '').toLowerCase().includes(term);
  });

  const handleCancel = async (mandateId) => {
    if (!window.confirm('Are you sure you want to cancel this mandate?')) return;
    try {
      await fetch(`${API_URL}/api/mandates/${mandateId}/cancel`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (onRefresh) onRefresh();
    } catch { alert('Failed to cancel mandate'); }
  };

  return (
    <div>
      <div className="p-4 border-b border-uf-border">
        <input type="text" placeholder="Search mandates by name or reference..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-4 py-2 text-uf-text text-sm" data-testid="mandate-search" />
      </div>
      <div className="admin-table-wrap">
      <table className="w-full">
        <thead>
          <tr className="bg-uf-surface-hi">
            <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Member</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Reference</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Account</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Bank</th>
            <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Amount</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Created</th>
            <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-uf-border">
          {filtered.length === 0 ? (
            <tr>
              <td colSpan="8" className="px-6 py-12 text-center text-uf-text-muted">
                {searchTerm ? 'No mandates match your search' : 'No mandates found'}
              </td>
            </tr>
          ) : (
            filtered.map((mandate) => (
              <tr key={mandate.id} className="hover:bg-uf-surface-hi">
                <td className="px-6 py-4">
                  <p className="text-uf-text text-sm font-medium">{mandate.member_name || mandate.account_holder_name || '-'}</p>
                  <p className="text-uf-text-muted text-xs">{mandate.member_email || ''}</p>
                </td>
                <td className="px-6 py-4 font-mono text-sm text-uf-text">{mandate.mandate_reference || mandate.bureau_mandate_id || '-'}</td>
                <td className="px-6 py-4 text-uf-text">
                  {mandate.account_holder_name}<br/>
                  <span className="text-uf-text-muted text-sm">{mandate.account_number_masked || `****${mandate.account_number_last4 || ''}`} | {mandate.sort_code_masked || mandate.sort_code || ''}</span>
                </td>
                <td className="px-6 py-4 text-uf-text-muted">{mandate.bank_name || '-'}</td>
                <td className="px-6 py-4 text-right text-uf-text font-medium">{mandate.monthly_amount ? `£${mandate.monthly_amount}` : '-'}</td>
                <td className="px-6 py-4">{getStatusBadge(mandate.status)}</td>
                <td className="px-6 py-4 text-uf-text-muted text-sm">
                  {mandate.created_at ? new Date(mandate.created_at).toLocaleDateString() : '-'}
                </td>
                <td className="px-6 py-4 text-right">
                  {mandate.status === 'active' && (
                    <button onClick={() => handleCancel(mandate.id)} className="px-3 py-1.5 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded text-xs" data-testid={`cancel-mandate-${mandate.id}`}>
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};

const ExceptionsTable = ({ exceptions, onRetry }) => (
  <div className="admin-table-wrap">
  <table className="w-full">
    <thead>
      <tr className="bg-uf-surface-hi">
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Member</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Amount</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Failure Reason</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Retries</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Actions</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-uf-border">
      {exceptions.length === 0 ? (
        <tr>
          <td colSpan="5" className="px-6 py-12 text-center text-uf-text-muted">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
            No exceptions - all payments up to date!
          </td>
        </tr>
      ) : (
        exceptions.map((exc) => (
          <tr key={exc.id} className="hover:bg-uf-surface-hi">
            <td className="px-6 py-4">
              <p className="text-uf-text font-medium">{exc.member_name || 'Unknown'}</p>
              <p className="text-uf-text-muted text-sm">{exc.member_email}</p>
            </td>
            <td className="px-6 py-4 text-right text-uf-text font-medium">£{exc.amount?.toFixed(2)}</td>
            <td className="px-6 py-4">
              <span className="text-red-400 font-mono text-sm">{exc.failure_code}</span>
              <p className="text-uf-text-muted text-sm">{exc.failure_reason}</p>
            </td>
            <td className="px-6 py-4 text-uf-text-muted">{exc.retry_count || 0}</td>
            <td className="px-6 py-4 text-right">
              <button 
                onClick={() => onRetry(exc.id)}
                className="px-3 py-1.5 bg-uf-red text-white rounded text-sm hover:bg-uf-red-hover"
              >
                Retry
              </button>
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
  </div>
);

export default AdminBilling;
